import { useIsMutationPending, useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { type ReviewRating } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import type { LearnCardActionTrigger } from "@-/dev.improve.haniverse.lib.learn.card-action";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

// Shared mutation key so every `useRatingMutation` consumer (digit keys, Enter/preset trigger,
// voice) observes the same in-flight review without lifting a callback.
const RATING_MUTATION_KEY = ["review", "rating"] as const;

export function useIsRating() {
  const isCommitted = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.transition.phase === "committed" || c.state.transition.phase === "exiting",
  )[0];
  return useIsMutationPending(RATING_MUTATION_KEY) || isCommitted;
}

/**
 * Rate the current card from a shortcut key or a voice command.
 *
 * A thin adapter over the deck's `commit`: the rating is one `rate` action through the same
 * pipeline the floating buttons and swipes use — the card parks, the write lands, the successor is
 * confirmed, and the card leaves in the rating's direction.
 */
export function useRatingMutation(trigger: LearnCardActionTrigger = "keyboard") {
  const [commit] = useContextSelectors(LearnDeckContext, (c) => c.commit);

  return useMutation({
    mutationFn: async (rating: ReviewRating) => {
      commit({ kind: "rate", rating }, trigger);
    },
    mutationKey: RATING_MUTATION_KEY,
  });
}
